<template>
  <!-- Use of 'unset' on width should ensure dialog is resized to contents: https://stackoverflow.com/questions/55085735/vuetify-v-dialog-dynamic-width -->
  <v-dialog
    v-if="visible"
    :value="visible"
    :width="width"
    :persistent="!closeable || !isDesktopView"
    scrollable
    :fullscreen="!isDesktopView"
    data-qa="bm_dialog"
    :style="{ 'z-index': 500 }"
    @input="visibleUpdatedByDialog"
  >
    <bm-card :label="label" :class="dialogType.dialogClass">
      <bm-form ref="form" v-model="valid" prevent-submit>
        <slot name="default" :valid="valid" />
      </bm-form>

      <template v-if="closeable" #menu>
        <bm-btn :x-large="large" icon button-type="ghost" @click="cancel">
          <v-icon data-qa="dialog_menu_close">highlight_off</v-icon>
        </bm-btn>
      </template>

      <template #controls>
        <slot
          name="controls"
          :valid="valid"
          :dirty="dirty"
          :accept="validateAndAccept"
          :cancel="cancel"
          :cancelable="cancelable"
          :dialog-type="dialogType"
        >
          <bm-dialog-controls
            :valid="valid"
            :dirty="dirty"
            :accept="validateAndAccept"
            :cancel="cancel"
            :cancelable="cancelable"
            :dialog-type="dialogType"
            :large="large"
          >
            <!-- Pass down all slots, for when only the text is overwritten -->
            <template v-for="(_, slot) in $scopedSlots" #[slot]="props">
              <slot :name="slot" v-bind="props" />
            </template>
          </bm-dialog-controls>
        </slot>
      </template>
    </bm-card>
  </v-dialog>
</template>

<script lang="ts">
import BmCard from "@/components/controls/card/BmCard.vue";
import BmDialogControls from "@/components/dialogs/BmDialogControls.vue";
import { useReactiveDialogSetup } from "@/components/mixins/dialogs";
import type { PropType } from "vue";
import { computed, defineComponent, ref } from "vue";
import { useFormValidator } from "@/components/mixins/validatable";
import { EnumDialogType } from "@/components/dialogs/DialogTypes";
import { useVuetify } from "@/plugins/vuetify";

// Defines key exported functions to be called externally
export type BmDialogInterface = {
  reset(): void;
  resetValidation(): void;
  validate(): boolean;
  isValid(): boolean;
};

/**
 * Generic BmDialog component
 *
 * Width = Pixel width or 'unset' to dynamically resize based on contents.
 *
 * @default: Primary 'OK' button, Secondary 'Cancel' button
 * @slots
 * 'controls', to completely replace the controls section
 *
 * @events
 * 'cancel', normally used to close the menu
 * 'accept', validates the form then confirm/submit the contents of the dialog.
 */
export default defineComponent({
  name: "BmDialog",
  components: { BmCard, BmDialogControls },
  props: {
    label: {
      type: String,
      required: true,
      default: "",
    },
    visible: {
      type: Boolean,
      required: true,
    },
    cancelable: {
      type: Boolean,
      required: false,
      default: true,
    },
    closeable: {
      type: Boolean,
      required: false,
      default: true,
    },
    width: {
      type: [Number, String],
      default: 600,
      required: false,
    },
    dirty: {
      type: Boolean,
      required: false,
      default: true, // Default true for dialogs non-reliant on dirty state
    },
    dialogType: {
      type: Object as PropType<EnumDialogType>,
      required: false,
      default: () => EnumDialogType.info,
    },
    large: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, context) {
    const { $vuetify } = useVuetify();
    const { valid, validate, form, reset, resetValidation } =
      useFormValidator();
    const { accept, cancel } = useReactiveDialogSetup(
      ref(props.visible),
      context
    );

    // useVuetify causes load sequence issues here that need sorting (it breaks dialogs in delivery app)
    const isDesktopView = computed(() => $vuetify.breakpoint.smAndUp ?? true);

    /**
     * This may be called by the Vuetify dialog if the user clicks outside the dialog - or the escape key is pressed.
     */
    const visibleUpdatedByDialog = (visible: boolean) => {
      if (!visible) {
        cancel();
      }
    };

    /**
     * Perform form validation before accepting.
     */
    const validateAndAccept = (payload?: any) => {
      if (validate()) {
        accept(payload);
      }
    };

    return {
      form,
      valid,
      validate,
      validateAndAccept,
      cancel,
      isDesktopView,
      visibleUpdatedByDialog,

      // Can be used externally
      reset,
      resetValidation,
      isValid: () => valid.value,
    };
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

:deep(.bm-card-error .bm-card-title), :deep(.bm-card-error .bm-card-title .v-icon)
  color: $bm-color-text04
  background-color: $bm-color-error-medium !important

:deep(.bm-card-warning .bm-card-title), :deep(.bm-card-warning .bm-card-title .v-icon)
  background-color: $bm-color-warning-medium !important

:deep(.bm-card-success .bm-card-title), :deep(.bm-card-success .bm-card-title .v-icon)
  color: $bm-color-text04
  background-color: $bm-color-success-medium !important

:deep(.bm-card-info .bm-card-title), :deep(.bm-card-info .bm-card-title .v-icon)
  color: $bm-color-text04
  background-color: $bm-color-info-medium !important
</style>
